import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutGrid, Home, ClipboardCheck, FileText, CalendarRange, Mail,
  ShieldCheck, ScrollText, Settings, Moon, Sun, LogOut, Building2, MoreHorizontal, X, Scale, KeyRound, BarChart3,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutGrid, end: true },
  { to: "/properties", label: "Properties", icon: Home },
  { to: "/inspections", label: "Inspections", icon: ClipboardCheck },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/schedule", label: "Scheduling", icon: CalendarRange },
  { to: "/notices", label: "Notices", icon: Mail },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/handovers", label: "Key Handovers", icon: KeyRound },
  { to: "/insights", label: "Response Insights", icon: BarChart3 },
  { to: "/tenancy-rules", label: "Tenancy Rules", icon: Scale },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings },
];

const PRIMARY = NAV.slice(0, 4); // Dashboard, Properties, Inspections, Reports
const MORE = NAV.slice(4);       // rest in the "More" sheet

export default function Layout({ children }) {
  const { user, agency, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 shrink-0 sticky top-0 h-screen border-r glass"
        style={{ borderColor: "var(--border)" }}
      >
        <div className="px-5 h-16 flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-xl grid place-items-center font-bold text-sm accent-fg overflow-hidden"
            style={{ background: agency?.logo_url ? "var(--surface-2)" : "var(--accent)" }}
          >
            {agency?.logo_url
              ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-contain" />
              : (agency?.brand_initials || <Building2 size={18} />)}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold truncate max-w-[150px]" style={{ color: "var(--text)" }}>
              {agency?.name || "PropPro"}
            </div>
            <div className="text-xs text-secondary">PropPro</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block"
            >
              {({ isActive }) => (
                <div
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  style={{
                    background: isActive ? "var(--accent-soft)" : "transparent",
                    color: isActive ? "var(--accent)" : "var(--text)",
                  }}
                >
                  <n.icon size={18} strokeWidth={2} />
                  {n.label}
                </div>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="h-8 w-8 rounded-full grid place-items-center text-white text-xs font-semibold"
              style={{ background: "var(--text-secondary)" }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{user?.name}</div>
              <div className="text-xs text-secondary capitalize">{user?.role?.replace(/_/g, " ")}</div>
            </div>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}>
              <LogOut size={16} className="text-secondary" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-30 h-14 flex items-center justify-between px-5 md:px-8 border-b glass lg:h-16"
          style={{ borderColor: "var(--border)" }}
        >
          <div className="lg:hidden flex items-center gap-2.5 font-semibold" style={{ color: "var(--text)" }}>
            <div className="h-8 w-8 rounded-lg grid place-items-center text-xs font-bold accent-fg overflow-hidden" style={{ background: agency?.logo_url ? "var(--surface-2)" : "var(--accent)" }}>
              {agency?.logo_url
                ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-contain" />
                : (agency?.brand_initials || "HC")}
            </div>
            <span className="text-sm truncate max-w-[180px]">{agency?.name || "PropPro"}</span>
          </div>
          <div className="flex-1" />
          <motion.button
            whileTap={{ scale: 0.9 }}
            data-testid="theme-toggle"
            onClick={toggle}
            className="h-9 w-9 rounded-full grid place-items-center"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
          >
            {dark ? <Sun size={16} style={{ color: "var(--warning)" }} /> : <Moon size={16} className="text-secondary" />}
          </motion.button>
        </header>

        <main className="flex-1 px-4 md:px-8 py-5 md:py-8 max-w-7xl w-full mx-auto pb-28 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t glass pb-safe" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-stretch justify-around px-1 pt-1.5 pb-1">
          {PRIMARY.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.label.toLowerCase()}`} className="flex-1">
              {({ isActive }) => (
                <div className="flex flex-col items-center gap-0.5 py-1 rounded-xl"
                  style={{ color: isActive ? "var(--accent)" : "var(--text-secondary)" }}>
                  <n.icon size={21} strokeWidth={isActive ? 2.4 : 2} />
                  <span className="text-[10px] font-semibold">{n.label}</span>
                </div>
              )}
            </NavLink>
          ))}
          <button data-testid="bottomnav-more" onClick={() => setMoreOpen(true)} className="flex-1">
            <div className="flex flex-col items-center gap-0.5 py-1" style={{ color: moreOpen ? "var(--accent)" : "var(--text-secondary)" }}>
              <MoreHorizontal size={21} />
              <span className="text-[10px] font-semibold">More</span>
            </div>
          </button>
        </div>
      </nav>

      {/* More sheet */}
      <AnimatePresence>
        {moreOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setMoreOpen(false)}
            className="lg:hidden fixed inset-0 z-50 flex items-end" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="more-sheet">
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 400, damping: 38 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded-t-3xl p-5 pb-safe border-t" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-full grid place-items-center text-white text-xs font-semibold" style={{ background: "var(--text-secondary)" }}>{initials}</div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>{user?.name}</div>
                    <div className="text-xs text-secondary capitalize">{user?.role?.replace(/_/g, " ")}</div>
                  </div>
                </div>
                <button onClick={() => setMoreOpen(false)} className="h-8 w-8 rounded-full grid place-items-center" style={{ background: "var(--surface-2)" }}><X size={16} className="text-secondary" /></button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {MORE.map((n) => (
                  <NavLink key={n.to} to={n.to} onClick={() => setMoreOpen(false)} data-testid={`more-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                    {({ isActive }) => (
                      <div className="flex flex-col items-center gap-1.5 py-3 rounded-2xl border"
                        style={{ borderColor: "var(--border)", background: isActive ? "var(--accent-soft)" : "var(--surface-2)", color: isActive ? "var(--accent)" : "var(--text)" }}>
                        <n.icon size={20} />
                        <span className="text-[11px] font-medium text-center leading-tight">{n.label}</span>
                      </div>
                    )}
                  </NavLink>
                ))}
                <button onClick={async () => { setMoreOpen(false); await logout(); nav("/login"); }} data-testid="more-logout">
                  <div className="flex flex-col items-center gap-1.5 py-3 rounded-2xl border" style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--danger)" }}>
                    <LogOut size={20} />
                    <span className="text-[11px] font-medium">Log out</span>
                  </div>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
